import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getLoggerInstance } from '../../../core/monitoring/logger';
import isNil from '../../../core/helpers/is-nil';
import { parseBarcodeData } from '../services/parse-barcode-data';
import { useStableFunction } from '../../../core/hooks/use-stable-function';
import { InlineNotification } from '../../../visual-components/inline-notification/inline-notification';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { ErrorSection } from '../../../visual-components/error-section/error-section';
import { setupScandit } from '../services/setup-scandit';
import { useTrackOnce } from '../../../modules/analytics/use-track-once';
import styles from './scanner.module.scss';

const logger = getLoggerInstance('scanner');

type Props = {
  onArticleIdDetect: (articleId: string) => void;
};

export const Scanner: React.FC<Props> = ({ onArticleIdDetect: instableOnArticleIdDetect }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const initRef = useRef(false);

  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const hasFailed = !isNil(error);

  const trackScannerStart = useTrackOnce({ type: 'scanner-started' });
  const trackScannerSuccess = useTrackOnce({ type: 'scanner-article-detected' });
  const onArticleIdDetect = useStableFunction(instableOnArticleIdDetect);

  useEffect(() => {
    const container = containerRef.current;
    if (isNil(container)) {
      logger.warn('Scanner container is not available.');
      return;
    }

    if (initRef.current === true) {
      return;
    }
    initRef.current = true;

    (async () => {
      await setupScandit({
        container,
        onBarcodeDetect: (barcode) => {
          const articleId = parseBarcodeData(barcode);
          if (isNil(articleId)) {
            return;
          }
          trackScannerSuccess();
          onArticleIdDetect(articleId);
        },
        reportError: (error) => {
          setError(error);
        },
        onInit: () => {
          trackScannerStart();
          setShowPlaceholder(false);
        },
      });
    })();
  }, [onArticleIdDetect, trackScannerStart, trackScannerSuccess]);

  return (
    <GridContainer>
      <GridItem>
        {showPlaceholder && !hasFailed ? (
          <InlineNotification text={t('scan.placeholder.text')} title={t('scan.placeholder.title')} type="info" />
        ) : null}
        {hasFailed ? (
          <>
            <InlineNotification text={t('scan.error.text')} title={t('scan.error.title')} type="error" />
            <ErrorSection debugInfo="Scan Setup Failed" error={error} />
          </>
        ) : null}
        <div
          ref={containerRef}
          className={classNames(styles.root, {
            [styles.visuallyHidden]: showPlaceholder,
          })}
        />
      </GridItem>
    </GridContainer>
  );
};
